/**
 * Shared "data-source mode" binding used by selection/list components (Dropdown,
 * RadioButtonsGroup, CheckboxGroup, ChipsSelector).
 *
 * - static  → the component's options are a hard-coded `[{ id, title, ... }]` array.
 * - dynamic → the options come from a data-exchange endpoint response, referenced as
 *             `${data.fieldName}`. Because `transformToWhatsApp` passes values through verbatim
 *             and `generateFlowJson` never mutates component props, emitting the string as the
 *             component's `data-source` is all that's needed.
 *
 * Pure half. The Static/Dynamic toggle and the reference field live in the
 * React-side `flow-components/dataSourceBinding.js`.
 */

export const DATA_SOURCE_MODE = 'data-source-mode';
export const DATA_SOURCE_REF = 'data-source-ref';

/** A blank/placeholder reference that should not be treated as a real binding yet. */
const REF_PLACEHOLDER = '${data.}';
const REF_PATTERN = /^\$\{data\..+\}$/;

/** Spread into a component's getDefaultProperties() to add the binding fields. */
export const defaultBindingProps = () => ({
  [DATA_SOURCE_MODE]: 'static',
  [DATA_SOURCE_REF]: REF_PLACEHOLDER,
});

export const isDynamic = (properties) => properties?.[DATA_SOURCE_MODE] === 'dynamic';

const trimmedRef = (properties) => (properties?.[DATA_SOURCE_REF] || '').trim();

/**
 * Resolve the value to emit for a component's `data-source` in the WhatsApp JSON:
 * the `${data.x}` string when dynamic with a non-blank ref, otherwise the static array.
 */
export const dataSourceForWhatsApp = (properties, staticArray) => {
  if (isDynamic(properties)) {
    const ref = trimmedRef(properties);
    if (ref) return ref;
  }
  return staticArray;
};

/**
 * Interpret a loaded/imported `data-source` value (for transformFromWhatsApp):
 * a string ⇒ dynamic binding; an array ⇒ static options.
 */
export const readLoadedDataSource = (rawDataSource) => {
  if (typeof rawDataSource === 'string') {
    return { mode: 'dynamic', ref: rawDataSource, array: [] };
  }
  return {
    mode: 'static',
    ref: REF_PLACEHOLDER,
    array: Array.isArray(rawDataSource) ? rawDataSource : [],
  };
};

/** Validate a dynamic binding; returns `{ errors, warnings }`. Static mode is always valid here. */
export const validateBinding = (properties) => {
  const errors = [];
  if (isDynamic(properties)) {
    const ref = trimmedRef(properties);
    if (!ref || ref === REF_PLACEHOLDER) {
      errors.push('Dynamic options need a data reference, e.g. ${data.options}.');
    } else if (!REF_PATTERN.test(ref)) {
      errors.push('Data reference must look like ${data.fieldName}.');
    }
  }
  return { errors, warnings: [] };
};
